import { BigNumberSchema, SerializableSchema, z } from "@repo/utils";
import { PurchaseOrderExtraCalculationSchema, PurchaseOrderExtraTypeSchema } from "../../../../erp/z.js";

export const ScalarComparisonResultSchema = z.enum(["equal", "different", "missing", "new"]);

export function createScalarComparisonSchema<T extends z.ZodMiniType>(valueSchema: T) {
    return z.object({
        result: ScalarComparisonResultSchema,
        prevValue: z.nullable(valueSchema),
        nextValue: z.nullable(valueSchema),
    });
}

export function createVectorComparisonSchema<
    Id1 extends z.ZodMiniType,
    Id2 extends z.ZodMiniType,
    T extends z.ZodMiniType,
>(id1: Id1, id2: Id2, datapoints: T) {
    return z.array(
        z.intersection(
            z.discriminatedUnion("result", [
                z.object({ result: z.literal("missing"), orderId: id1, confirmationId: z.null() }),
                z.object({ result: z.literal("new"), orderId: z.null(), confirmationId: id2 }),
                z.object({ result: z.literal("matched"), orderId: id1, confirmationId: id2 }),
            ]),
            z.object({
                datapoints,
            }),
        ),
    );
}

export const ScheduleLineSchema = z.object({
    quantity: BigNumberSchema,
    deliveryDate: z.nullable(z.string()),
});

export const ConfirmationLineSchema = z.object({
    quantity: BigNumberSchema,
    deliveryDate: z.nullable(z.string()),
    reference: z.nullable(z.string()),
});

export const ExtraLineSchema = z.object({
    calculation: PurchaseOrderExtraCalculationSchema,
    type: PurchaseOrderExtraTypeSchema,
    name: z.optional(z.string()),
    description: z.optional(z.string()),
});

export const PurchaseOrderComparisonSchema = z
    .object({
        paymentTerms: createScalarComparisonSchema(z.string()),
        incoterms1: createScalarComparisonSchema(z.string()),
        incoterms2: createScalarComparisonSchema(z.string()),
        items: createVectorComparisonSchema(
            z.string(),
            z.number(),
            z.object({
                unitPrice: createScalarComparisonSchema(BigNumberSchema),
                netUnitPrice: createScalarComparisonSchema(BigNumberSchema),
                quantity: createScalarComparisonSchema(BigNumberSchema),
                deliveryAddress: createScalarComparisonSchema(z.string()),
                supplierSku: createScalarComparisonSchema(z.string()),
                confirmations: createScalarComparisonSchema(z.array(ConfirmationLineSchema)),
                grossTotalPrice: createScalarComparisonSchema(BigNumberSchema),
                netTotalPrice: createScalarComparisonSchema(BigNumberSchema),
                extra: createScalarComparisonSchema(z.array(ExtraLineSchema)),

                scheduleLines: z.array(ScheduleLineSchema),
                ignoredExtra: z.array(ExtraLineSchema),
            }),
        ),
        grossTotalPrice: createScalarComparisonSchema(BigNumberSchema),
        netTotalPrice: createScalarComparisonSchema(BigNumberSchema),
        extra: createScalarComparisonSchema(z.array(ExtraLineSchema)),

        ignoredExtra: z.array(ExtraLineSchema),
        arbitrary: z.record(z.string(), SerializableSchema),
    })
    .register(z.globalRegistry, { title: "PurchaseOrderComparisonSchema" });
